import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { voiceApi } from '@/api/services/voice.api';
import { handleMutationError } from '@/lib/handle-mutation-error';
import type { IListRecordingsQuery, IPresignedRecordingUrl } from '../types';
import { voiceKeys } from '../query-keys';

export function useRecordings(params?: IListRecordingsQuery) {
    return useQuery({
        queryKey: voiceKeys.recordings.list(params),
        queryFn: () => voiceApi.listRecordings(params),
        placeholderData: keepPreviousData,
        staleTime: 15_000,
    });
}

export function useMarkRecordingHeard() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: voiceApi.markRecordingHeard,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.recordings.all });
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to mark as heard'),
    });
}

export function useDeleteRecording() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: voiceApi.deleteRecording,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.recordings.all });
            toast.success('Recording deleted');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to delete recording'),
    });
}

/**
 * Fetches a fresh presigned URL on demand (e.g. when the user hits play).
 * `fetcher` swaps the route — an AI-answered call's saved recording (P9-8)
 * is not a softphone recording row.
 */
export function useFetchRecordingUrl(
    fetcher: (id: string) => Promise<IPresignedRecordingUrl> = voiceApi.getRecordingUrl,
) {
    return useMutation({
        mutationFn: fetcher,
        onError: (error: unknown) => handleMutationError(error, 'Failed to load the recording'),
    });
}
